import IDataComponent from 'src/models/IDataComponent'
import IPipeline, { backfillNameSuffix, PipelineType } from 'src/models/IPipeline'

/** A pipeline with no type is a workflow, as the API defaults it (MEL-702). */
export const pipelineType = (pipeline: Partial<IPipeline>): PipelineType =>
    pipeline.type ?? PipelineType.Workflow

const TYPE_LABELS: Record<PipelineType, string> = {
    [PipelineType.Ingestion]: 'Ingestion',
    [PipelineType.Workflow]: 'Workflow',
}

export const pipelineTypeLabel = (type: PipelineType): string => TYPE_LABELS[type]

/** A backfill is an ingestion named for the ingestion that it backfills, with the backfill suffix. */
export const isBackfill = (pipeline: Partial<IPipeline>): boolean =>
    pipelineType(pipeline) === PipelineType.Ingestion && !!pipeline.name?.endsWith(backfillNameSuffix)

/**
 * The part between the ingestion's name and the suffix only makes each name unique, so any part without a dot
 * will do, or none. Without the dot, a backfill of `<ingestion>.eu` is not one of `<ingestion>`.
 */
export const isBackfillOf = (ingestion: IPipeline, pipeline: IPipeline): boolean =>
    isBackfill(pipeline) && pipeline.name.startsWith(`${ingestion.name}.`)
    && !pipeline.name.slice(ingestion.name.length + 1, -backfillNameSuffix.length).includes('.')

const TYPE_DESCRIPTIONS: Record<PipelineType, string> = {
    [PipelineType.Ingestion]: 'Copy data from one source into your warehouse. Pick the streams and set a schedule.',
    [PipelineType.Workflow]: 'Run dbt, data tests, alerts or any other tool. Run it after your ingestions finish, on a schedule, or both.',
}

/** What a pipeline of the type does, for someone choosing which to create. */
export const pipelineTypeDescription = (type: PipelineType): string => TYPE_DESCRIPTIONS[type]

const TYPE_EXAMPLES: Record<PipelineType, string> = {
    [PipelineType.Ingestion]: 'e.g. Salesforce → Warehouse, daily',
    [PipelineType.Workflow]: 'e.g. dbt deps, dbt run after salesforce + orders-db',
}

export const pipelineTypeExample = (type: PipelineType): string => TYPE_EXAMPLES[type]

const PLUGIN_TYPE_BY_PREFIX: Record<string, string> = {
    extractors: 'EXTRACTOR',
    loaders: 'LOADER',
    mappers: 'MAPPER',
    transformers: 'TRANSFORMER',
    utilities: 'UTILITY',
    files: 'FILE',
}

/**
 * A pipeline component's plugin type, upper case (`EXTRACTOR`, `LOADER` …). Taken from its embedded
 * plugin, else from the component's own field, else from a plugin name such as
 * `extractors/tap-x--y` given for a component not created yet.
 */
export const componentPluginType = (dc: Partial<IDataComponent>): string | undefined =>
    (dc._embedded?.dataplugin?.pluginType
        || dc.dataPluginType
        || PLUGIN_TYPE_BY_PREFIX[dc.name?.split('/')[0] || ''])
        ?.toUpperCase()

/** The plugin types an ingestion may hold: one extractor, one loader, and any mappers between. */
export const INGESTION_PLUGIN_TYPES = ['EXTRACTOR', 'MAPPER', 'LOADER']

/**
 * The name of an ingestion's own extractor, `<source>--<pipeline name>`, kept to what Meltano allows
 * (as the API's `Pipeline.ownExtractorName`, MEL-702).
 */
export const ownExtractorName = (source: string, pipelineName: string): string =>
    `${source}--${pipelineName.toLowerCase().replace(/[^a-z0-9_-]+/g, '-').replace(/^-+|-+$/g, '')}`

/**
 * Whether a pipeline's streams can be discovered without Verify's connection test: an ingestion's, or a
 * workflow's that has a tap and a target (as the API's `Pipeline.canDiscover`, MEL-702).
 */
export const canDiscover = (type: PipelineType, components: Partial<IDataComponent>[]): boolean =>
    type === PipelineType.Ingestion
        || (components.some(dc => componentPluginType(dc) === 'EXTRACTOR') && components.some(dc => componentPluginType(dc) === 'LOADER'))

/**
 * The positions of a workflow's data stores (loaders) that have no source (extractor) before them:
 * `meltano run` reads a loader as the end of the extractor before it, so such a workflow fails.
 */
export const loadersWithoutSource = (components: Partial<IDataComponent>[]): number[] => {
    const positions: number[] = []
    let hasSource = false
    components.forEach((dc, i) => {
        const pluginType = componentPluginType(dc)
        if (pluginType === 'EXTRACTOR')
            hasSource = true
        else if (pluginType === 'LOADER') {
            !hasSource && positions.push(i)
            hasSource = false
        }
    })
    return positions
}

/**
 * A workflow's components with one added: at the end, except a source, which goes before the first data
 * store with no source before it - e.g. a tap added after the warehouse runs before it. The order the
 * workflow already has is kept (MEL-702).
 */
export const withComponent = <T extends Partial<IDataComponent>>(components: T[], component: T): T[] => {
    const before = componentPluginType(component) === 'EXTRACTOR' ? loadersWithoutSource(components)[0] : undefined
    return before === undefined
        ? [...components, component]
        : [...components.slice(0, before), component, ...components.slice(before)]
}
